import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Filter, StickyNote, X } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface NoteItem {
  id: string;
  title: string;
  tag_ids: string[];
  scope_ids: string[];
  created_at: string;
  updated_at: string;
}

interface Tag { id: string; name: string; color: string; }

export function NotesPage() {
  const toast = useToast();
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterTag, setFilterTag] = useState('');

  async function loadData() {
    try {
      const params = new URLSearchParams();
      if (filterTag) params.set('tag_id', filterTag);
      params.set('sort_by', 'updated_at');
      params.set('sort_dir', 'desc');

      const [n, t] = await Promise.all([
        apiGet<NoteItem[]>(`/notes?${params}`),
        apiGet<Tag[]>('/tags'),
      ]);
      setNotes(n);
      setTags(t);
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, [filterTag]);

  async function handleCreate(title: string) {
    try {
      await apiPost('/notes', { title });
      setShowCreate(false);
      toast.success('Note created');
      loadData();
    } catch {
      toast.error('Failed to create note');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this note?')) return;
    try {
      await apiDelete(`/notes/${id}`);
      toast.success('Note deleted');
      loadData();
    } catch {
      toast.error('Failed to delete note');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  const tagMap = new Map(tags.map(t => [t.id, t]));

  return (
    <div className="h-full overflow-auto p-6">
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Notes</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Note
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Filter size={14} className="text-text-muted" />
        <select
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          className="px-2 py-1 bg-bg-input border border-border rounded text-sm text-text-primary"
        >
          <option value="">All Tags</option>
          {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {filterTag && (
          <button onClick={() => setFilterTag('')} className="text-xs text-text-muted hover:text-text-primary">
            Clear filter
          </button>
        )}
      </div>

      {/* Note List */}
      {notes.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <StickyNote size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No notes found</p>
          <p className="text-sm">Create a note to get started.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notes.map((note) => (
            <div key={note.id} className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group">
              <StickyNote size={16} className="text-[#14b8a6] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <Link
                  to={`/notes/${note.id}`}
                  className="text-sm text-text-primary hover:text-accent transition-colors"
                >
                  {note.title}
                </Link>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-text-muted">
                    {formatRelativeTime(note.updated_at)}
                  </span>
                  {note.tag_ids.map(tid => {
                    const tag = tagMap.get(tid);
                    return tag ? (
                      <span key={tid} className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                        {tag.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <button
                onClick={() => handleDelete(note.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-text-muted hover:text-danger transition-all"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateNoteModal onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
    </div>
  );
}

function CreateNoteModal({
  onSave,
  onClose,
}: {
  onSave: (title: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">New Note</h2>
        <div>
          <label className="block text-sm text-text-secondary mb-1">Title *</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            autoFocus
            placeholder="What's this note about?"
            onKeyDown={e => { if (e.key === 'Enter' && title.trim()) onSave(title.trim()); }}
            className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button
            onClick={() => title.trim() && onSave(title.trim())}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
